/**
 * Bare PSI wrapper around react-aria-components' `Tooltip` + `TooltipTrigger`.
 * Intended as a building block for higher-level tooltip wrappers, not for
 * direct use in product code.
 */
import { useTheme } from '@repo/theme';
import { Tooltip as AriaTooltip, type TooltipProps as AriaTooltipProps, TooltipTrigger as AriaTooltipTrigger } from 'react-aria-components';

import { BaseOverlayArrow } from './base-overlay-arrow';
import styles from './base-tooltip.module.css';

export type BaseTooltipProps = {
    text: string;
    children: React.ReactNode;
    delay?: number;
    closeDelay?: number;
    defaultOpen?: boolean;
    isDisabled?: boolean;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
} & Omit<AriaTooltipProps, 'children'>;

/** @public */
export const DEFAULT_TOOLTIP_DELAY = 750;
const DEFAULT_TOOLTIP_CLOSE_DELAY = 150;

export function BaseTooltip({
    text,
    closeDelay = DEFAULT_TOOLTIP_CLOSE_DELAY,
    delay = DEFAULT_TOOLTIP_DELAY,
    defaultOpen,
    isDisabled,
    isOpen,
    onOpenChange,
    children,
    ...props
}: BaseTooltipProps) {
    const { colors } = useTheme();
    return (
        <AriaTooltipTrigger
            closeDelay={closeDelay}
            defaultOpen={defaultOpen}
            delay={delay}
            isDisabled={isDisabled}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
        >
            {children}
            <AriaTooltip className={styles.tooltip} offset={8} {...props}>
                <BaseOverlayArrow className={styles.arrow} fill={colors.colorSurfacePopoverDark} />
                {text}
            </AriaTooltip>
        </AriaTooltipTrigger>
    );
}
